import { ReactElement } from 'react';
import classNames from 'classnames';
import { RowColoring, TableColumn, TableRow } from './TableRow';
import { TableHeader, TableSorting } from './TableHeader';
import { NoDataPlaceholder } from '../NoDataPlaceholder';
import { ContentLoaderRow } from './ContentLoaderRow';

type RowMeta = {
  key: string;
  coloring?: RowColoring;
};
export type RowData<T> = T & RowMeta;

type TableProps<T> = {
  columns: TableColumn<T>[];
  rowData: RowData<T>[];
  onRowClicked: (key: string) => void;
  sorting?: TableSorting<T>;
  isLoading?: boolean;
  highlightedRow?: string;
};

export const Table = <T extends object>({
  columns,
  rowData,
  onRowClicked,
  sorting,
  isLoading = false,
  highlightedRow,
}: TableProps<T>): ReactElement | null => {
  const isRowHighlighted = (key: string) => {
    return highlightedRow === key;
  };

  const renderRow = (key: string, row: RowData<T>) => {
    return (
      <TableRow
        key={key}
        columns={columns}
        model={row}
        onClick={() => onRowClicked(key)}
        highlighted={isRowHighlighted(key)}
        coloring={row.coloring}
      />
    );
  };

  if (!isLoading && rowData.length === 0) {
    return <NoDataPlaceholder className={'max-width-100pct height-100pct'} />;
  }

  return (
    <div id="table-container">
      <table
        className={classNames(
          'table',
          'table-column-overflow-hidden',
          'table-bordered',
          'table-sticky',
          'table-head-filled',
          'table-hover',
          'table-layout-fixed',
        )}
      >
        <TableHeader
          columns={columns}
          sorting={sorting}
        />
        <tbody>
          {isLoading && rowData.length === 0 && (
            <ContentLoaderRow columns={columns} />
          )}
          {rowData.map((row) => {
            const key = row.key;
            return renderRow(key, row);
          })}
        </tbody>
      </table>
    </div>
  );
};
